/**
 * Reimbursements Routes
 *
 * The ReimbursementJourney component handles its own internal tab routing for:
 *   - summary (index), payments, non-payments, appeals, reconsiderations,
 *     overpayments, reconsideration-outcomes
 *
 * The following pages are NOT handled internally and need their own route entries:
 *   - payment-integrity / payment-integrity-page (PaymentIntegrityComponent)
 *   - payment-integrity-page/payment-integrity-details (PaymentIntegrityDetailsComponent)
 *   - settlements (Settlement)
 *   - view-top-claims (ViewTopClaims component)
 */

import { lazy } from 'react';
import { RouteConfig, RouteMetadata } from '../../types';

const ReimbursementJourney = lazy(
  () => import('@src/apps/Reimbursement/ReimbursementJourney/ReimbursementJourneyComponent')
);
const PaymentIntegrity = lazy(() =>
  import('@src/apps/Reimbursement/PaymentIntegrity/PaymentIntegrityComponent').then((m) => ({
    default: m.PaymentIntegrityComponent,
  }))
);
const PaymentIntegrityDetails = lazy(() =>
  import(
    '@src/apps/Reimbursement/PaymentIntegrity/paymentIntegrityDetails/PaymentIntegrityDetailsComponent'
  ).then((m) => ({
    default: m.PaymentIntegrityDetailsComponent,
  }))
);
const Settlement = lazy(() =>
  import('@src/apps/Reimbursement/Settlement/Settlement').then((m) => ({ default: m.Settlement }))
);
const ViewTopClaims = lazy(() =>
  import('@src/apps/Reimbursement/ViewTopClaims/ViewTopClaims.Component').then((m) => ({
    default: m.ViewTopClaimsComponent,
  }))
);

const EXTERNAL_ACCESS_DENIED: Pick<RouteMetadata, 'externalAccess'> = {
  externalAccess: false,
};

export const reimbursementsRoutes: RouteConfig[] = [
  // Payment Integrity — separate parcel, not inside ReimbursementJourney
  {
    path: 'reimbursements/payment-integrity',
    component: PaymentIntegrity,
    guards: ['auth', 'vendor'],
    data: {
      ...EXTERNAL_ACCESS_DENIED,
      title: 'Medical Records Coding Review',
      breadcrumb: 'Medical Records Coding Review',
    },
  },
  {
    path: 'reimbursements/payment-integrity-page',
    component: PaymentIntegrity,
    guards: ['auth', 'vendor'],
    data: {
      ...EXTERNAL_ACCESS_DENIED,
      title: 'Payment Integrity',
      breadcrumb: 'Payment Integrity',
    },
  },
  {
    path: 'reimbursements/payment-integrity-page/payment-integrity-details',
    component: PaymentIntegrityDetails,
    guards: ['auth', 'vendor'],
    data: {
      ...EXTERNAL_ACCESS_DENIED,
      title: 'Payment Integrity Details',
      breadcrumb: 'Payment Integrity Details',
    },
  },
  // Settlements — separate parcel
  {
    path: 'reimbursements/settlements',
    component: Settlement,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'Settlements', breadcrumb: 'Settlements' },
  },
  // View Top Claims — separate parcel
  {
    path: 'reimbursements/view-top-claims',
    component: ViewTopClaims,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'Top Claims', breadcrumb: 'Top Claims' },
  },
  // Wildcard fallback — ReimbursementJourney handles internal tabs
  {
    path: 'reimbursements/*',
    component: ReimbursementJourney,
    guards: ['auth', 'vendor'],
    data: { ...EXTERNAL_ACCESS_DENIED, title: 'Reimbursements', breadcrumb: 'Reimbursements' },
  },
];
